import { cn } from '@/lib/utils';
import { Eye, EyeOff, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Translator, { useTranslation } from '@/components/i18n/Translator';

import Alert from './Alert';
import { ProviderButton } from './ProviderButton';

interface Props {
  error?: string;
  providers: string[];
  callbackUrl: string;
  onPasswordSignIn?: (
    email: string,
    password: string,
    callbackUrl: string
  ) => Promise<any>;
  onOAuthSignIn?: (provider: string, callbackUrl: string) => Promise<any>;
}

interface FormValues {
  email: string;
  password: string;
}

export function LoginForm({
  providers,
  onPasswordSignIn,
  onOAuthSignIn,
  callbackUrl,
  error
}: Props) {
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(error);
  const [showPassword, setShowPassword] = useState(false);

  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: ''
    }
  });
  const phone = watch('email', '');

  useEffect(() => {
    setErrorState(error);
  }, [error]);

  const onSubmit = async (data: FormValues) => {
    if (!onPasswordSignIn) return;

    setLoading(true);
    try {
      await onPasswordSignIn(data.email, data.password, callbackUrl);
    } catch (err) {
      if (err instanceof Error) {
        setErrorState(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // 状态管理
  const [countdown, setCountdown] = useState(0); // 倒计时秒数
  const [isSending, setIsSending] = useState(false); // 是否正在发送

  // 发送验证码逻辑
  const handleSendCode = async () => {
    // 防止重复点击
    if (isSending || countdown > 0) return;

    try {
      // 标记为发送中
      setIsSending(true);

      // 验证手机号合法性
      if (!phone.trim()) {
        const errorMsg = '请输入手机号';
        alert(errorMsg);
        return;
      }

      // 简单手机号格式校验（中国大陆）
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (!phoneRegex.test(phone.trim())) {
        const errorMsg = '请输入有效的手机号';
        alert(errorMsg);
        return;
      }
      
      // 调用发送验证码API（替换为你的实际接口）
      const response = await fetch('/chainlit/send-verification-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() }) // 需从表单获取实际手机号
      });

      if (!response.ok) throw new Error('发送失败');
      // 解析后端返回的 JSON 数据（无论成功或失败）
      const result = await response.json();
      // 判断请求是否成功
      if (!result.success) {
        // 提取后端返回的错误信息，默认为通用提示
        alert(result.message)
        return
      }


      // 发送成功后启动倒计时（60秒）
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (error) {
      console.error('验证码发送失败:', error);
      alert('验证码发送失败，请稍后重试');
    } finally {
      // 无论成功失败，都结束"发送中"状态
      setIsSending(false);
    }
  };

  const oAuthReady = onOAuthSignIn && providers.length;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-6')}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">
          <Translator path="auth.login.title" />
        </h1>
      </div>

      {errorState && (
        <Alert variant="error">
          {t([
            `auth.login.errors.${errorState.toLowerCase()}`,
            `auth.login.errors.default`
          ])}
        </Alert>
      )}

      <div className="grid gap-6">
        {onPasswordSignIn && (
          <>
            <div className="grid gap-2">
              <Label htmlFor="email">
                手机号
              </Label>
              <Input
                id="email"
                disabled={loading}
                autoFocus
                placeholder="请输入手机号"
                {...register('email', {
                  required: "手机号不能为空"
                })}
                className={cn(
                  touchedFields.email && errors.email && 'border-destructive'
                )}
              />
              {touchedFields.email && errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">
                  验证码
                </Label>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  disabled={loading}
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: "验证码不能为空",
                    minLength: { value: 6, message: '验证码长度为6位' },
                    maxLength: { value: 6, message: '验证码长度为6位' },
                    pattern: { value: /^\d+$/, message: '验证码必须为数字'}
                  })}
                  className={cn(
                    touchedFields.password &&
                      errors.password &&
                      'border-destructive'
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={handleSendCode}
                  disabled={isSending || countdown > 0} // 发送中或倒计时中禁用
                >
                  {countdown > 0 ? (
                    // 倒计时状态显示剩余秒数和时钟图标
                    <>
                      <Clock className="h-4 w-4 mr-1" />
                      <span className="text-xs">{countdown}s</span>
                    </>
                  ) : isSending ? (
                    // 发送中状态显示加载图标（可替换为Spinner）
                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    // 正常状态显示"发送验证码"文本或图标
                    <span className="text-xs">获取验证码</span>
                  )}
                </Button>
              </div>
              {touchedFields.password && errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              <Translator path="auth.login.form.actions.signin" />
            </Button>
          </>
        )}

        {onPasswordSignIn && oAuthReady ? (
          <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
            <span className="relative z-10 bg-background px-2 text-muted-foreground">
              <Translator path="auth.login.form.alternativeText.or" />
            </span>
          </div>
        ) : null}

        {oAuthReady ? (
          <div className="grid gap-2">
            {providers.map((provider, index) => (
              <ProviderButton
                key={`provider-${index}`}
                provider={provider}
                onClick={() => onOAuthSignIn?.(provider, callbackUrl)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </form>
  );
}
